import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import openSocket from "../../services/socket-io";
import { getToken, setToken, clearToken, wipeAllBrowserState } from "../../services/tokenStore";
import { setVolumeChatInterno, setChatInternoNotifVolume01 } from "../../services/chatInternoSom";
import { setRingtoneVolume, setRingtoneNotifVolume01 } from "../../services/ringtone";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { reassinarPushSeAutorizado, desativarPush } from "../../services/pwa";

// Interceptors must be registered synchronously (not inside a useEffect),
// otherwise child components can fire requests before they're attached,
// racing the request past the Authorization header. Since useAuth is only
// ever instantiated once (the root AuthProvider), a module-level guard
// keeps registration idempotent across re-renders without deferring it.
let interceptorsRegistered = false;
let refreshPromise = null;

// Referências compartilhadas (módulo-nível): interceptors (que rodam fora
// do hook React) e listeners do socket que precisam tocar em state do hook.
// Sem isto, uma função definida dentro do bloco `if (!interceptorsRegistered)`
// era inacessível pelo listener do socket que está DENTRO do hook — o toast
// aparecia mas o logout real NUNCA rodava (ReferenceError / escopo quebrado).
let onSessionExpired = () => {};
let setUserRef = () => {};
let sessaoJaEncerrada = false;
let toastKickMostrado = false;

const resetarSessaoFlags = () => {
  sessaoJaEncerrada = false;
  toastKickMostrado = false;
};

// Única função de logout. Chamada de: interceptor 401 (requisição HTTP),
// interceptor 403 refresh vencido, socket.on(session:kicked_out).
// Independente de quem chamou, SEMPRE dá clearToken, SEMPRE desliga isAuth,
// SEMPRE manda para o login. Usa flag de dedup para não executar duas vezes
// (solução para os DOIS toasts que apareciam quando backend emitia duas
// vezes o evento).
const encerrarSessao = (motivo = "expired") => {
  if (sessaoJaEncerrada) return;
  sessaoJaEncerrada = true;

  // Mostrar o toast correto para o motivo.
  if (motivo === "kicked_out" && !toastKickMostrado) {
    toastKickMostrado = true;
    toast.error("Sua conta foi logada em outro local");
  } else if (motivo === "maintenance" && !toastKickMostrado) {
    toastKickMostrado = true;
    toast.info("Sistema em manutenção no momento.");
  }

  // Apenas limpa o token da memória e o header do axios. NÃO apaga cookies
  // client-side / localStorage / sessionStorage aqui — NÃO é ação explícita
  // do usuário (foi disparado por interceptor de 401/403 ou socket).
  // Motivo: o antigo wipeAllBrowserState() aqui dentro do interceptor
  // causava LOOP INFINITO de reloads.
  try {
    clearToken();
  } catch (_) { /* noop */ }
  try {
    api.defaults.headers.Authorization = undefined;
  } catch (_) { /* noop */ }

  // Fluxo "limpo" via React (navigate do router + setIsAuth).
  // É A FORMA PRINCIPAL de levar pro login.
  try {
    onSessionExpired(motivo);
  } catch (_) { /* noop */ }

  // Fallback SÓ SE realmente não está na página de login ainda.
  // Não pode ficar disparando location.href de 200 em 200ms na página
  // de login — causa o loop infinito que quebrou hoje.
  const jahEstaNoLogin =
    typeof window !== "undefined" &&
    String(window.location.pathname || "") === "/login";

  if (!jahEstaNoLogin) {
    setTimeout(() => {
      // NÃO resetar sessaoJaEncerrada = false aqui. Reseta-la a cada
      // encerrarSessao foi exatamente o que causou o loop. A flag fica
      // true e só volta false via useEffect([isAuth=true]) no login.
      try {
        window.location.href = "/login";
      } catch (_) { /* noop */ }
    }, 250);
  }
};

if (!interceptorsRegistered) {
  interceptorsRegistered = true;

  api.interceptors.request.use(
    config => {
      const token = getToken();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // O backend usa 403 para duas coisas diferentes: token de acesso vencido
  // (mensagem "Invalid token...") e falta de permissão (ERR_NO_PERMISSION,
  // ERR_USER_INACTIVE, ERR_READ_ONLY_USER). Só o primeiro pede renovação —
  // tentar renovar num 403 de permissão gasta uma chamada à toa e some com
  // o erro que o usuário precisava ver.
  const ehTokenVencido = resposta => {
    const motivo =
      resposta?.data?.error || resposta?.data?.message || "";

    return (
      resposta?.status === 403 &&
      !String(motivo).startsWith("ERR_NO_PERMISSION") &&
      !String(motivo).startsWith("ERR_USER_INACTIVE") &&
      !String(motivo).startsWith("ERR_READ_ONLY_USER")
    );
  };

  api.interceptors.response.use(
    response => {
      return response;
    },
    async error => {
      const originalRequest = error.config;
      const requestUrl = String(originalRequest?.url || "");
      const ehRotaPublicaDeAuth =
        requestUrl.includes("/auth/login") ||
        requestUrl.includes("/auth/register-company") ||
        requestUrl.includes("/auth/signup") ||
        requestUrl.includes("/auth/maintenance");

      // A própria renovação falhando não pode entrar em recursão.
      const ehChamadaDeRefresh = String(originalRequest?.url || "").includes(
        "/auth/refresh_token"
      );

      // Renovar o token só faz sentido numa requisição autenticada. Sem token
      // em memória e sem cabeçalho Authorization, o 403 que chegou é a
      // resposta de verdade da própria rota — cadastro desativado, permissão,
      // bloqueio do proxy — e tentar renovar ali trocava essa mensagem pela
      // falha do refresh: como o /auth/refresh_token responde 401
      // ERR_SESSION_EXPIRED para quem não tem sessão, o cadastro público
      // exibia "Sessão expirada. Por favor entre." no lugar do motivo real.
      const pedidoAutenticado =
        Boolean(getToken()) || Boolean(originalRequest?.headers?.Authorization);

      if (
        ehTokenVencido(error?.response) &&
        pedidoAutenticado &&
        !originalRequest._retry &&
        !ehChamadaDeRefresh
      ) {
        originalRequest._retry = true;

        if (!refreshPromise) {
          refreshPromise = api
            .post("/auth/refresh_token")
            .then(({ data }) => {
              if (data) {
                // Um lugar só: quem precisa do token (axios e socket)
                // escuta o tokenStore.
                setToken(data.token);
                api.defaults.headers.Authorization = `Bearer ${data.token}`;
                if (data.user) {
                  setUserRef(prev => {
                    const novo = {
                      ...(data.user || {}),
                      queues: Array.isArray(data.user.queues)
                        ? data.user.queues
                        : Array.isArray(prev?.queues) ? prev.queues : [],
                    };
                    return novo;
                  });
                }
              }
              return data;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        try {
          await refreshPromise;
        } catch (falhaAoRenovar) {
          encerrarSessao("expired");
          return Promise.reject(falhaAoRenovar);
        }

        return api(originalRequest);
      }

      // 401 nem sempre quer dizer "sua sessão acabou".
      //
      // Qualquer rota que confira senha responde 401 quando ela não bate —
      // e derrubar a sessão por isso expulsava do sistema quem só errou a
      // digitação numa confirmação. Foi o que acontecia ao gerar backup.
      //
      // A sessão só cai quando o erro é da própria sessão. Erro sem código
      // reconhecido continua derrubando: é o caso do token ausente ou
      // inválido, em que o backend responde antes de qualquer regra.
      const ERROS_DE_SESSAO = [
        "ERR_SESSION_EXPIRED",
        "ERR_INVALID_TOKEN",
        "ERR_USER_INACTIVE",
        "ERR_SESSION_KICKED_OUT"
      ];

      if (error?.response?.status === 401) {
        const codigo = String(
          error?.response?.data?.error || error?.response?.data?.message || ""
        );

        const ehBlob = error?.response?.data instanceof Blob;

        // Só existe "sessão expirada" se havia uma sessão. Numa tela pública
        // (login, cadastro, link salvo) o app dispara chamadas sem token — o
        // /auth/refresh_token que confere o cookie no boot e o
        // /subscriptions/mine que a moeda da plataforma consulta — e todas
        // respondem 401 ERR_SESSION_EXPIRED. Sem esta guarda o interceptor
        // tratava isso como fim de sessão e expulsava quem só abriu o
        // /signup: aparecia o toast de sessão expirada e a tela pulava para o
        // login. No /login o sintoma ficava escondido (o aviso só aparece
        // fora dali), por isso o defeito só era visto no "Cadastre-se".
        const tinhaSessao = Boolean(getToken());

        if (
          tinhaSessao &&
          (!codigo || ehBlob || ERROS_DE_SESSAO.some(e => codigo.startsWith(e)))
        ) {
          const motivo = codigo === "ERR_SESSION_KICKED_OUT" ? "kicked_out" : "expired";
          encerrarSessao(motivo);
        }
      }

      if (
        error?.response?.status === 503 &&
        String(error?.response?.data?.error || "") === "ERR_SAAS_MAINTENANCE" &&
        !ehRotaPublicaDeAuth
      ) {
        encerrarSessao("maintenance");
      }

      return Promise.reject(error);
    }
  );
}

// ---- Helpers de persistência LOCALSTORAGE para volume notificações ----
// Chave é POR USUÁRIO (userId incluso). Assim se 2 pessoas logarem no
// mesmo navegador cada uma mantém sua própria preferência sem conflitar.
// O localStorage é um "cache resiliente": a fonte da verdade CONTINUA
// sendo a coluna Users.notificationVolume do banco; este cache só serve
// para: (a) aplicar volume instantâneo antes do fetch /me terminar;
// (b) fallback caso a sessão esteja temporariamente offline/rápida;
// (c) garantir memória quando o usuário retorna após fechar o browser.
const LS_VOLUME_PREFIX = "PERABIT_NOTIF_VOLUME_v1_";
const chaveLsVolume = (userId) => `${LS_VOLUME_PREFIX}${Number(userId) || 0}`;

const lerLsVolume = (userId) => {
  try {
    const raw = localStorage.getItem(chaveLsVolume(userId));
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.min(100, n));
  } catch (_) {
    return null;
  }
};
const gravarLsVolume = (userId, valor) => {
  try {
    if (!userId) return;
    const v = Math.max(0, Math.min(100, Number(valor) || 0));
    localStorage.setItem(chaveLsVolume(userId), String(v));
  } catch (_) { /* noop (storage bloqueado, privado, etc.) */ }
};

const useAuth = () => {
  const aplicarVolumeGlobal = (usuario) => {
    // ================================================================
    // BUG || 50 ERA AQUI TBEM: usuario.notificationVolume === 0
    // (mudo) virava 50 por causa de (0 || 50 === 50). Setters então
    // enviavam 50% para os módulos chatInternoSom/ringtone.
    // Solução: checagem EXPLÍCITA de undefined/null, NÃO do valor
    // numérico. 0 é um valor VÁLIDO e deve ser mantido.
    // ================================================================
    const valorDoBanco =
      usuario !== undefined &&
      usuario !== null &&
      typeof usuario?.notificationVolume !== "undefined" &&
      usuario?.notificationVolume !== null
        ? Number(usuario.notificationVolume)
        : 50;
    const v = Math.max(0, Math.min(100, valorDoBanco));
    const fator = v / 100;

    // Camada 1: volume BASE por tipo de som (valores históricos hardcoded antes):
    //   - chat interno som bip = 0.22
    //   - ringtone de chamada = 0.25
    // Mantidos fixos. O ajuste PROPORCIONAL 0..100% entra na variável SEPARADA
    // `notificationVolume01` do próprio módulo, que multiplica a base NA HORA
    // do play (early return se 0).
    setVolumeChatInterno(0.22);
    setRingtoneVolume(0.25);

    // Camada 2 (OVERRIDE INDEPENDENTE por preferência de usuário 0..100%).
    // Chamado SEMPRE APÓS os setters de base (ordem garantida):
    //   resultado final = volume_base * notificationVolume01
    // Exemplos:
    //   slider 0%  → chat 0.22 * 0 = 0, ring 0.25 * 0 = 0  (SILENCIO TOTAL)
    //   slider 50% → chat 0.22 * 0.5 = 0.11, ring 0.125
    //   slider 100%→ chat 0.22 * 1 = 0.22, ring 0.25 (originais)
    //
    // Passamos userId no 2º parâmetro para QUE O PRÓPRIO MÓDULO grave o
    // localStorage também (deduplicação segura: o módulo já lê storage na
    // sua inicialização; ao gravar lá também, o próximo F5 já inicia no
    // valor correto ANTES MESMO do useAuth montar).
    const uid = Number(usuario?.id || 0) || null;
    setChatInternoNotifVolume01(fator, { userId: uid });
    setRingtoneNotifVolume01(fator, { userId: uid });
  };

  const normalizarUsuario = (u) => {
    const userId = Number(u?.id || 0);
    // ================================================================
    // BUG ABSOLUTO QUE MATAVA 0%: a condição "doBanco > 0" abaixo de
    // NADA servia pois se usuário SALVOU 0% (MUDO) no banco o valor é
    // ZERO e ZERO > 0 === FALSE, forçando cair no fallback 50 default!
    // Era EXATAMENTE o sintoma VERBATIM: "1% sai quase nada, 0% toca
    // normal". 1% (1) passava por (doBanco > 0). 0% (0) NÃO PASSAVA.
    // SOLUÇÃO: só verificar undefined/null (presença do campo), NÃO
    // mais o valor numérico. 0 agora é preservado.
    // ================================================================
    const campoExisteNoPayload =
      u !== undefined &&
      u !== null &&
      typeof u?.notificationVolume !== "undefined" &&
      u?.notificationVolume !== null;
    if (campoExisteNoPayload) {
      const doBanco = Math.max(0, Math.min(100, Number(u?.notificationVolume) || 0));
      // VALOR PRESERVADO INDEPENDENTE DE SER 0 OU NÃO:
      return {
        ...(u || {}),
        notificationVolume: doBanco,
        queues: Array.isArray(u?.queues) ? u.queues : [],
        roles: Array.isArray(u?.roles) ? u.roles : [],
      };
    }
    const doLs = userId ? lerLsVolume(userId) : null;
    const fallback = Number.isFinite(doLs) ? doLs : 50;
    return {
      ...(u || {}),
      notificationVolume: fallback,
      queues: Array.isArray(u?.queues) ? u.queues : [],
      roles: Array.isArray(u?.roles) ? u.roles : [],
    };
  };
  const obterValorSetting = (lista, chave, padrao = "enabled") => {
    const item = (lista || []).find(
      (s) => s && String(s.key || "").toLowerCase() === String(chave || "").toLowerCase(),
    );
    if (!item || typeof item.value === "undefined" || item.value === null) return padrao;
    return String(item.value);
  };

  const navigate = useNavigate();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(normalizarUsuario({}));
  const [globalSettings, setGlobalSettings] = useState([]);

  // Após qualquer login bem-sucedido, as flags de dedup precisam voltar
  // ao zero. Senão um kickout antigo impediria um logout novo de funcionar.
  useEffect(() => {
    if (isAuth) resetarSessaoFlags();
  }, [isAuth]);

  // Aplica o volume notificação em CHAT INTERNO + RINGTONE assim que o
  // usuário loga / recarrega a página / a preferência muda via socket
  // ou via toggle na UI. Para os players useSound (sound.mp3) o volume
  // é aplicado diretamente dentro de NotificationCenter / NotificationsPopOver
  // usando a prop "volume" do hook (eles reagem a user.notificationVolume).
  //
  // Também mantém o localStorage SINCRONIZADO com a fonte da verdade (DB):
  // sempre que user.notificationVolume ou user.id mudarem (login, socket
  // user update, PUT perfil, patch volume) gravamos no cache local.
  useEffect(() => {
    if (user?.id) {
      aplicarVolumeGlobal(user);
      gravarLsVolume(user.id, user?.notificationVolume);
    }
  }, [user?.id, user?.notificationVolume]);

  // Linka as referências compartilhadas com os reals setters deste hook.
  // É isto que faz o interceptor e o listener do socket conseguirem
  // realmente manipular o state sem estar "dentro" do componente.
  useEffect(() => {
    setUserRef = setUser;
  }, [setUser]);

  const carregarSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      if (Array.isArray(data)) setGlobalSettings(data);
    } catch (_) {
      setGlobalSettings([]);
    }
  };
  const atualizarGlobalSetting = (setting) => {
    if (!setting || !setting.key) return;
    setGlobalSettings((prev) => {
      const arr = Array.isArray(prev) ? prev.slice() : [];
      const idx = arr.findIndex((s) => s && s.key === setting.key);
      if (idx === -1) {
        arr.push({ ...setting });
      } else {
        arr[idx] = { ...(arr[idx] || {}), ...setting };
      }
      return arr;
    });
  };
  const isSettingEnabled = (chave, padrao = "enabled") => {
    return obterValorSetting(globalSettings, chave, padrao) !== "disabled";
  };

  useEffect(() => {
    onSessionExpired = (motivo) => {
      const estavaLogado = window.location.pathname !== "/login";

      setIsAuth(false);
      setUser(normalizarUsuario({}));

      if (estavaLogado) {
        // No kickout, o toast já foi mostrado por encerrarSessao(). Não
        // duplicar o toast informativo "sessão expirada" em cima do erro
        // real da outra conta logando em outro dispositivo.
        if (motivo !== "kicked_out" && motivo !== "maintenance") {
          toast.info(i18n.t("auth.toasts.expired"));
        }
        navigate("/login");
      }
    };
  }, [navigate]);

  // Ao abrir a página, quem diz se há sessão é o cookie httpOnly `jrt` — o
  // token de acesso vive só na memória e some a cada recarga. Por isso a
  // tentativa é sempre feita: não há mais nada no navegador para consultar
  // antes.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/auth/refresh_token");
        setToken(data.token);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        setUser(normalizarUsuario(data.user));
        carregarSettings();
      } catch (err) {
        // Sem cookie válido é só uma visita não autenticada — a tela de
        // login é o destino normal, não um erro para mostrar na cara de
        // quem ainda nem tentou entrar.
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    const onUser = data => {
      if (data.action === "update" && data.user.id === user.id) {
        setUser(normalizarUsuario(data.user));
      }
    };

    const onKickedOut = (dados) => {
      const uid = Number(user?.id || 0);
      const veioComUid = Number(dados?.userId || 0);
      const ehMesmoUsuario = !veioComUid || !uid || veioComUid === uid;

      if (!ehMesmoUsuario) return;

      encerrarSessao("kicked_out");
    };

    const onSettings = data => {
      if (!data || !data.setting || !data.setting.key) return;
      if (data.action === "update" || data.action === "create" || !data.action) {
        atualizarGlobalSetting(data.setting);
      } else if (data.action === "destroy" || data.action === "delete") {
        setGlobalSettings(prev => (prev || []).filter(s => !(s && s.key === data.setting.key)));
      }
    };

    socket.on("user", onUser);
    socket.on("session:kicked_out", onKickedOut);
    socket.on("settings", onSettings);

    return () => {
      try { socket.off("user", onUser); } catch (_) {}
      try { socket.off("session:kicked_out", onKickedOut); } catch (_) {}
      try { socket.off("settings", onSettings); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  // O `loading` NÃO é ligado aqui, e isso é de propósito.
  //
  // Ele significa "a sessão ainda está sendo restaurada", e o PublicRoute
  // troca a tela inteira por um BackdropLoading enquanto for true. Usar o
  // mesmo sinal para "o formulário está enviando" desmontava e remontava a
  // tela de login a cada tentativa — o que zerava a etapa do código de
  // verificação assim que ela aparecia. Quem mostra que o envio está em
  // curso é o `enviando`, que é local da tela e não mexe na árvore.
  const handleLogin = async userData => {
    try {
      resetarSessaoFlags();
      const { data } = await api.post("/auth/login", userData);
      setToken(data.token);
      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      setUser(normalizarUsuario(data.user));
      setIsAuth(true);
      carregarSettings();

      // Se este aparelho já autorizou notificações antes, reassina sozinho
      // para a conta que acabou de entrar — o logout desassina de propósito,
      // justamente para uma conta não receber aviso da outra no mesmo celular.
      reassinarPushSeAutorizado().catch(() => {});

      toast.success(i18n.t("auth.toasts.success"));

      // Senha temporária (o admin combinou ou gerou): em vez do sistema, cai
      // na tela bloqueante de troca até definir a própria senha.
      if (data?.user?.mustChangePassword) {
        navigate("/trocar-senha", { replace: true });
        return;
      }

      navigate("/tickets");
    } catch (err) {
      // A conta tem segundo fator e o código ainda não veio. Não é erro
      // para quem está entrando: é a segunda etapa do login. Sem o
      // `throw` a tela não teria como saber que precisa pedir os seis
      // dígitos, e o toast vermelho diria "não autorizado" a quem
      // digitou a senha certa.
      if (err?.response?.data?.error === "ERR_2FA_REQUIRED") {
        const erro = new Error("ERR_2FA_REQUIRED");
        erro.precisa2FA = true;
        throw erro;
      }

      if (err?.response?.data?.error === "ERR_SAAS_MAINTENANCE") {
        throw err;
      }

      toastError(err);
      throw err;
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    // Desassina o push DESTE aparelho antes de limpar a sessão: sem isso a
    // próxima pessoa a logar aqui continuaria recebendo aviso da conta
    // anterior, furando o isolamento entre usuários e empresas.
    try {
      await desativarPush();
    } catch (_) {}

    try {
      await api.delete("/auth/logout");
    } catch (err) {
      toastError(err);
    } finally {
      // NÃO resetar sessaoJaEncerrada = false aqui. Se resetar antes do
      // location.href, no reload da pagina o AuthProvider monta de novo e
      // uma chamada a /auth/me falhando 401 dispara encerrarSessao de novo,
      // que dispara outro location.href → LOOP INFINITO. Deixa true; só
      // volta false via useEffect([isAuth=true]) quando o login submeter.
      toastKickMostrado = false;
      setIsAuth(false);
      setUser(normalizarUsuario({}));
      setGlobalSettings([]);
      clearToken();
      api.defaults.headers.Authorization = undefined;

      // LIMPEZA TOTAL: SÓ AQUI (ação explícita do usuário clicando em Sair).
      // Em encerrarSessao (interceptor 401/403) NÃO chamamos mais —
      // evita o loop infinito que quebrou hoje.
      try {
        wipeAllBrowserState();
      } catch (_) { /* noop */ }

      setLoading(false);

      const jahEstaNoLogin =
        typeof window !== "undefined" &&
        String(window.location.pathname || "") === "/login";

      if (!jahEstaNoLogin) {
        try {
          window.location.href = "/login";
        } catch (_) {
          navigate("/login");
        }
      } else {
        // Se já estamos no login (por exemplo, clicou em Sair dentro de uma
        // tab que já tava nessa página), não força reload — desliga loading
        // e deixa o React montar o login por transição normal.
        navigate("/login");
      }
    }
  };

  // Salva a preferência do atendente: assinar ou não o nome nas mensagens.
  // Vai direto para o banco via PATCH — persistindo fora do localStorage
  // (navegador diferente, limpar cookies etc continua igual). O próprio
  // backend também envia "user" pelo socket, e o listener ali em cima
  // atualiza o contexto automaticamente quando chega.
  const handleUpdateSignMessages = async signMessages => {
    if (!user || !user.id) return { signMessages };
    try {
      const { data } = await api.patch(`/users/${user.id}/sign-messages`, { signMessages: Boolean(signMessages) });
      setUser(u => normalizarUsuario({ ...(u || {}), signMessages: data.signMessages }));
      return data;
    } catch (err) {
      toastError(err);
      return { signMessages };
    }
  };

  // Preferência individual: silenciar SOMENTE o som de notificação de
  // tickets de grupo (Ticket.isGroup=true). Conversas individuais
  // continuam tocando normalmente. Persistida no banco igual signMessages.
  const handleUpdateMuteGroupSounds = async muteGroupSounds => {
    if (!user || !user.id) return { muteGroupSounds };
    try {
      const { data } = await api.patch(`/users/${user.id}/mute-group-sounds`, { muteGroupSounds: Boolean(muteGroupSounds) });
      setUser(u => normalizarUsuario({ ...(u || {}), muteGroupSounds: data.muteGroupSounds }));
      return data;
    } catch (err) {
      toastError(err);
      return { muteGroupSounds };
    }
  };

  // Preferência individual de intensidade do som de notificações.
  // Range 0..100, padrão 50. Aplica em 4 players:
  //   1) NotificationCenter / NotificationsPopOver (useSound sound.mp3)
  //   2) chatInternoSom (som chat interno Web Audio API)
  //   3) ringtone (chamada telefônica Web Audio API)
  const handleUpdateNotificationVolume = async notificationVolume => {
    if (!user || !user.id) return { notificationVolume };
    const valor = Math.max(0, Math.min(100, Number(notificationVolume) || 0));
    try {
      const { data } = await api.patch(`/users/${user.id}/notification-volume`, { notificationVolume: valor });
      const valorSalvo = Math.max(0, Math.min(100, Number(data.notificationVolume) || 0));
      setUser(u => normalizarUsuario({ ...(u || {}), notificationVolume: valorSalvo }));
      return data;
    } catch (err) {
      toastError(err);
      return { notificationVolume: valor };
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    handleUpdateSignMessages,
    handleUpdateMuteGroupSounds,
    handleUpdateNotificationVolume,
    globalSettings,
    isSettingEnabled,
    atualizarGlobalSetting,
    carregarSettings,
  };
};

export default useAuth;
